import { styled } from '@linaria/react';
import { Flex } from '@mantine/core';
import * as Sentry from '@sentry/browser';
import { useNavigate, useSearchParams } from 'react-router';
import { useSystemAccessCreate } from '../../../api/integrations-client/integrations-client.hooks';
import { IntegrationAuthType } from '../../../api/integrations-client/integrations-client.type';
import { useDocumentTitle } from '../../../helpers/general-helpers';
import { capitalizeFirstLetter } from '../../../helpers/string-helpers/string-helpers';
import { useOrgId } from '../../../store/global-store/global-store.hooks';
import { useIntegrationsStore } from '../../../store/integrations-store/integrations-store';
import { setIntegration, updateIntegration } from '../../../store/integrations-store/integrations-store.actions';
import { baseWhite } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Divider } from '../../../ui-library/divider/divider';
import { Icon } from '../../../ui-library/icon/icon';
import { H3, Text } from '../../../ui-library/typography/typography';
import { MenuDrivenContainer } from '../../menu-driven-container/menu-driven-container';
import { encodeBasicAuth, formatLogoSrc } from './add-new-integration.helpers';
import type {
  BasicAuthForm as BasicAuthFormType,
  TokenAuthForm as TokenAuthFormType,
} from './add-new-integration.type';
import { BasicAuthForm } from './forms/basic-auth-form';
import { TokenAuthForm } from './forms/token-auth-form';

export const AddNewIntegrationAuthorize = () => {
  useDocumentTitle('Authorize Integration - Bloomfilter');

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const baseUrl = searchParams.get('baseUrl');

  const integration = useIntegrationsStore((state) => state.integration);
  const integrationName = capitalizeFirstLetter(integration?.name ?? '');
  const orgId = useOrgId();

  const { mutateAsync: createSystemAccess } = useSystemAccessCreate();

  const submitSystemAccess = async (token: string) => {
    if (!integration || !orgId) {
      return;
    }

    const { id } = integration;

    try {
      const result = await createSystemAccess({ id, orgId, token });
      if (result) {
        updateIntegration({ baseUrl, system_access_id: result.id });
        navigate(`/application/integrations/success`);
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  /**
   * Handles the submission of the basic authentication form.
   *
   * @param {BasicAuthFormType} values - The values from the basic authentication form.
   * @returns {Promise<void>} - A promise that resolves when the submission is complete.
   */
  const handleBasicAuthSubmit = async (values: BasicAuthFormType): Promise<void> => {
    const { email, password } = values;

    const token = encodeBasicAuth(email, password);

    await submitSystemAccess(token);
  };

  /**
   * Handles the submission of the token authentication form.
   *
   * @param {TokenAuthFormType} values - The values from the token authentication form.
   * @returns {Promise<void>} - A promise that resolves when the submission is complete.
   */
  const handleTokenAuthSubmit = async (values: TokenAuthFormType): Promise<void> => {
    const { email, apiToken } = values;

    const token = encodeBasicAuth(email, apiToken);

    await submitSystemAccess(token);
  };

  const header = (
    <Flex align="center" gap={16}>
      <Button
        variant="secondary"
        radius="xl"
        leftSection={<Icon name="arrow_back" size={18} color={baseWhite} />}
        onClick={() => {
          setIntegration(null);
          navigate('/application/integrations');
        }}
      >
        Back
      </Button>
      <H3>Add New Integrations</H3>
    </Flex>
  );

  if (!integration) {
    return null;
  }

  return (
    <MenuDrivenContainer header={header}>
      <PageContainer>
        <Flex direction="column" gap={8}>
          <Text size="large" lineHeight="none" weight="bold" style={{ padding: 10 }}>
            {integrationName}
          </Text>
          <Divider />
        </Flex>
        <Flex direction="column" gap={32} miw={400} style={{ margin: '0 auto' }}>
          <Flex gap={16}>
            <IntegrationIcon src={formatLogoSrc(integration.logo)} alt={integration.name} />
            <H3>Authorize {integrationName}</H3>
          </Flex>
          {integration?.auth_type === IntegrationAuthType.Basic ? (
            <BasicAuthForm onSubmit={handleBasicAuthSubmit} />
          ) : (
            <TokenAuthForm onSubmit={handleTokenAuthSubmit} />
          )}
        </Flex>
      </PageContainer>
    </MenuDrivenContainer>
  );
};

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 150px;
  box-sizing: border-box;
  padding: 16px;
  background: #fff;
  border-radius: 8px;
  height: 100%;
  overflow-y: auto;
`;

const IntegrationIcon = styled.img`
  width: 32px;
  height: 32px;
`;
